<script setup lang="ts">
import { provide, ref, computed } from 'vue';
import { SecondaryButton, ModalDialog, AllowedRoundedSize, useTranslation } from '@hfc/ui-core';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import FeedbackForm from '@/shared/infrastructure/components/FeedbackForm.vue';
import ThankYou from '@/shared/infrastructure/components/ThankYou.vue';
import { AppStore } from '@/shared/infrastructure/store/AppStore';

provide('AppStore', new AppStore());

const { t } = useTranslation();

const props = withDefaults(defineProps<{
  label?: string;
  displayMode?: 'button' | 'link';
  extraParams?: unknown;
  applicationType?: string;
  uuid?: string;
  token?: string;
}>(), {
  displayMode: 'button',
});

const castApplicationType = computed(() => props.applicationType as APPLICATION_TYPES ?? undefined);
const showModal = ref(false);
const showFeedbackForm = ref(true);
const showThankYou = ref(false);

let localExtraParams: unknown;
if (props.extraParams && typeof props.extraParams === 'string') {
  try {
    localExtraParams = JSON.parse(props.extraParams);
  } catch {
    // do nothing
  }
}

const closeAllViews = (): void => {
  showFeedbackForm.value = false;
  showThankYou.value = false;
};

const feedbackSubmitSuccess = (): void => {
  closeAllViews();
  showThankYou.value = true;
};

const handleModalClose = (): void => {
  showModal.value = false;
  showFeedbackForm.value = true;
  showThankYou.value = false;
};

const translatedLabel = computed(() => props.label ?? t('feedback.label'));
</script>
<template>
  <LinkButton v-if="displayMode === 'link'" @click="showModal = true" type="button">
    <slot>
      {{ translatedLabel }}
    </slot>
  </LinkButton>
  <SecondaryButton v-else :label="translatedLabel" class="fixed top-1/2 -right-1 transform -rotate-90 -translate-y-10 origin-bottom-right" :rounded="AllowedRoundedSize.BASIC" outline outline-border-size="border" @click="showModal = true" />
  <ModalDialog v-if="showModal === true" @close="handleModalClose" size="md" :full-height="true" :title="t('feedback.label')" :show-footer="false" :depth=2000 class="cursor-default" style="zoom: 1;">
    <template #content>
      <FeedbackForm v-if="showFeedbackForm" @feedback-success="feedbackSubmitSuccess" :show-title="false" :show-shadow="false" :extra-params="localExtraParams" :application-type="castApplicationType" :uuid="uuid" :token="token" />
      <ThankYou v-if="showThankYou" :show-title="false" :show-shadow="false" />
    </template>
  </ModalDialog>
</template>
